import LegalShell from "./LegalShell";

export default function SvaDetoxTerms() {
  return (
    <LegalShell title="Terms of Service" product="sva-detox">
      <p>
        These Terms of Service ({'"'}Terms{'"'}) form a binding agreement between you and <strong>BastionGuard Medical Technologies Pvt. Ltd.</strong> ({'"'}Company{'"'}, {'"'}we{'"'}, {'"'}us{'"'}). By creating an account or using the <strong>Sva — Detox</strong> mobile application or any related services (collectively, the {'"'}Service{'"'}), you agree to these Terms.
      </p>

      <h2>1. The Service</h2>
      <p>Sva — Detox is a digital-wellness and parental-control mobile application that helps users limit their phone usage, schedule phone-free windows, practise mindfulness, and gate access to selected apps via multi-step challenges. It also offers an AI-powered coaching feature ({'"'}Sva{'"'}) that suggests calming activities and reflections.</p>

      <h2>2. Eligibility & Accounts</h2>
      <ul>
        <li>You must be <strong>18 years or older</strong> to create an account.</li>
        <li>A parent or legal guardian may create a Child / Self account on behalf of a minor; the parent is solely responsible for the minor{"'"}s use of the Service.</li>
        <li>You agree to provide accurate registration information including a working <strong>recovery email</strong>.</li>
        <li>You are responsible for keeping your password, daily passcode, and Privileged Password confidential.</li>
      </ul>

      <h2>3. Subscription, Free Trial & Billing</h2>

      <h3>3.1 Plan</h3>
      <div className="overflow-x-auto">
        <table>
          <thead><tr><th>Plan</th><th>Price</th><th>Trial</th><th>Renewal</th></tr></thead>
          <tbody><tr><td>Sva — Detox Pro</td><td><strong>₹199 / month</strong> (incl. 18% GST)</td><td>7 days free</td><td>Monthly auto-renew via Razorpay</td></tr></tbody>
        </table>
      </div>

      <h3>3.2 Free trial</h3>
      <p>Your first 7 days are free of charge. You may cancel any time during the trial from Profile → Subscription; no amount will be debited.</p>

      <h3>3.3 Auto-renewal</h3>
      <p>Unless cancelled at least <strong>24 hours</strong> before the end of the trial or the current billing period, the subscription will <strong>auto-renew at ₹199/month</strong> (inclusive of GST) through your selected Razorpay payment method.</p>

      <h3>3.4 GST tax invoice</h3>
      <p>After every successful payment, a GST-compliant tax invoice is generated and emailed to your registered email address. If you provide a valid GSTIN at checkout, it will appear on the invoice. Invoices are retained for 8 years.</p>

      <h3>3.5 Price changes</h3>
      <p>We may revise pricing with at least 30 days{"'"} notice. Continued use after the new price takes effect constitutes acceptance.</p>

      <h2>4. Cancellation & Refund Policy</h2>

      <h3>4.1 Cancellation</h3>
      <p>You can cancel at any time from <strong>Profile → Subscription → Cancel</strong> inside the App, or by emailing <a href="mailto:info@bastionguardhealthcare.in">info@bastionguardhealthcare.in</a>. Cancellation takes effect at the end of the current billing period; you retain access until then.</p>

      <h3>4.2 Refunds</h3>
      <p>Because the Service is a digital subscription with a 7-day free trial during which the entire product can be evaluated, <strong>we do not provide pro-rata refunds for partially-used billing cycles</strong>. Refund requests will be considered on a case-by-case basis only for:</p>
      <ul>
        <li>Duplicate or accidental charges, or</li>
        <li>Documented service outages preventing all use of the Service,</li>
      </ul>
      <p>and must be raised within <strong>14 days</strong> of the charge by emailing <a href="mailto:info@bastionguardhealthcare.in">info@bastionguardhealthcare.in</a>. Approved refunds are processed through Razorpay to the original payment method within 5–10 working days.</p>

      <h2>5. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Reverse-engineer, decompile, or extract source code from the App.</li>
        <li>Use the Service for any unlawful purpose or to harass, stalk or harm any person.</li>
        <li>Resell, sublicense or commercially distribute the Service or its AI outputs.</li>
        <li>Attempt to bypass our security, the Privileged Password gate, or our payment systems.</li>
        <li>Submit any harmful, defamatory, infringing or pornographic content to the AI coach.</li>
      </ul>

      <h2>6. Intellectual property</h2>
      <p>The App, its source code, designs, brand, logo and {'"'}Sva — Detox{'"'} name are owned by BastionGuard Medical Technologies Pvt. Ltd. and protected by Indian and international IP law. You receive only a limited, non-exclusive, non-transferable licence to use the App for your personal, non-commercial purposes during an active subscription.</p>

      <h2>7. AI Coach disclaimers</h2>
      <p>The in-app coach <strong>{'"'}Sva{'"'}</strong> uses a large language model and may occasionally produce incorrect or inappropriate suggestions. Coaching content is for <strong>general wellness purposes only</strong> and is <strong>not</strong>:</p>
      <ul>
        <li>Medical, psychological, psychiatric or therapeutic advice.</li>
        <li>A substitute for consultation with a qualified healthcare professional.</li>
      </ul>
      <p>If you are experiencing a mental-health crisis, please contact a qualified professional or India{"'"}s iCALL helpline at <strong>9152987821</strong>.</p>

      <h2>8. Native device features (Android)</h2>
      <p>Some advanced blocking features require Android-specific permissions (Usage Stats, Accessibility, Display-over-other-apps, Device Admin). You grant these permissions voluntarily, and the App functions in a reduced {'"'}in-app challenge{'"'} mode if you do not grant them. You can revoke any permission at any time from Android Settings.</p>

      <h2>9. Disclaimer of warranties</h2>
      <p>The Service is provided <strong>{'"'}as is{'"'}</strong> and <strong>{'"'}as available{'"'}</strong>. To the maximum extent permitted by law, BastionGuard Medical Technologies Pvt. Ltd. disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>

      <h2>10. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, our total aggregate liability arising out of or relating to the Service in any 12-month period shall not exceed the total subscription fees actually paid by you to us during that period. We are not liable for indirect, incidental, special, consequential or punitive damages.</p>

      <h2>11. Indemnity</h2>
      <p>You agree to indemnify and hold harmless BastionGuard Medical Technologies Pvt. Ltd., its directors, employees and contractors from any claim, loss, liability or expense arising out of your breach of these Terms or your misuse of the Service.</p>

      <h2>12. Suspension & termination</h2>
      <p>We may suspend or terminate your account at our discretion if you breach these Terms, attempt fraud, or repeatedly fail to pay subscription charges. You may terminate at any time by deleting your account.</p>

      <h2>13. Governing law & jurisdiction</h2>
      <p>These Terms are governed by the laws of <strong>India</strong>. Subject to the arbitration clause below, the courts at <strong>Pathanamthitta, Kerala</strong> shall have exclusive jurisdiction.</p>

      <h2>14. Dispute resolution</h2>
      <p>Any dispute arising from these Terms shall first be attempted to be resolved through good-faith discussions for 30 days. If unresolved, the dispute shall be referred to a sole arbitrator under the Arbitration and Conciliation Act, 1996. The seat and venue of arbitration shall be <strong>Pathanamthitta, Kerala</strong>, and the language shall be English.</p>

      <h2>15. Changes to these Terms</h2>
      <p>We may modify these Terms at any time. Material changes will be notified in-app and via email at least 7 days before they take effect. Continued use of the Service constitutes acceptance of the revised Terms.</p>

      <h2>16. Contact</h2>
      <p>BastionGuard Medical Technologies Pvt. Ltd.<br />372/E 1, Karunya Building, Podiyadi, Pathanamthitta – 689110, Kerala, India<br />Email: <a href="mailto:info@bastionguardhealthcare.in">info@bastionguardhealthcare.in</a></p>
    </LegalShell>
  );
}
